import React from 'react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import {
  User, MessageCircle, Loader2, Bell, ChevronLeft, Send,
  Briefcase, Upload, FileText
} from 'lucide-react';
import { formatPriceInput, parsePriceInput, formatPrice } from '../../utils/format';

export const MessageDialog = ({
  open, onOpenChange, listing, owner,
  messageText, setMessageText,
  sendingMessage, onSend
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="sm:max-w-md">
      <DialogHeader>
        <DialogTitle>Send Message to Listing Owner</DialogTitle>
        <DialogDescription>Send a message about the listing "{listing?.title}"</DialogDescription>
      </DialogHeader>
      <div className="space-y-4 py-4">
        <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
          <Avatar className="h-10 w-10">
            <AvatarImage src={owner?.picture || owner?.profile_photo_url} />
            <AvatarFallback className="bg-emerald-900 text-white"><User className="w-5 h-5" /></AvatarFallback>
          </Avatar>
          <div>
            <p className="font-medium text-sm">{owner?.name || 'Listing Owner'}</p>
            <p className="text-xs text-muted-foreground">Listing Owner</p>
          </div>
        </div>
        <Textarea placeholder="Type your message..." value={messageText} onChange={(e) => setMessageText(e.target.value)} rows={4} data-testid="message-textarea" />
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
        <Button onClick={onSend} disabled={sendingMessage || !messageText.trim()} className="bg-emerald-600 hover:bg-[#2980b9]" data-testid="send-message-btn">
          {sendingMessage ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <MessageCircle className="w-4 h-4 mr-2" />}
          Send
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export const PriceAlertDialog = ({
  open, onOpenChange, listing,
  targetPriceDisplay, setTargetPrice, setTargetPriceDisplay,
  alertType, setAlertType, savingAlert, onSave
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Bell className="w-5 h-5 text-amber-500" />Set Price Alert
        </DialogTitle>
        <DialogDescription>We'll notify you when the price drops to your set level.</DialogDescription>
      </DialogHeader>
      <div className="space-y-4 py-4">
        <div>
          <label className="text-sm font-medium mb-2 block">Current Price</label>
          <p className={listing?.price ? 'text-2xl font-bold text-[#f97316]' : 'text-xs text-slate-400 font-medium'}>
            {formatPrice(listing?.price, listing?.price_type)}
          </p>
        </div>
        <div>
          <label className="text-sm font-medium mb-2 block">Target Price (£)</label>
          <input type="text" inputMode="numeric" value={targetPriceDisplay || ''}
            onChange={(e) => {
              const formatted = formatPriceInput(e.target.value);
              setTargetPrice(parsePriceInput(formatted));
              setTargetPriceDisplay(formatted);
            }}
            className="w-full px-3 py-2 border rounded-lg" placeholder="e.g.: 50.000" data-testid="target-price-input" />
        </div>
        <div>
          <label className="text-sm font-medium mb-2 block">Alert Type</label>
          <div className="flex gap-2">
            <Button variant={alertType === 'below' ? 'default' : 'outline'} size="sm" onClick={() => setAlertType('below')} className={alertType === 'below' ? 'bg-emerald-600' : ''}>When price drops</Button>
            <Button variant={alertType === 'any_change' ? 'default' : 'outline'} size="sm" onClick={() => setAlertType('any_change')} className={alertType === 'any_change' ? 'bg-emerald-600' : ''}>On any change</Button>
          </div>
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
        <Button onClick={onSave} disabled={savingAlert} className="bg-amber-500 hover:bg-amber-600" data-testid="create-alert-btn">
          {savingAlert ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Bell className="w-4 h-4 mr-2" />}
          Alarm Kur
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export const ImageLightbox = ({ open, onOpenChange, images, index, setIndex, title }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-[95vw] max-h-[95vh] p-0 bg-black/95 border-none">
      <div className="relative w-full h-full flex flex-col">
        <button onClick={() => onOpenChange(false)} className="absolute top-4 right-4 z-50 bg-white/10 hover:bg-white/20 rounded-full p-2 transition-colors" data-testid="lightbox-close-btn">
          <span className="text-white text-2xl">&times;</span>
        </button>
        {images?.length > 1 && (
          <div className="absolute top-4 left-4 z-50 bg-black/50 text-white px-3 py-1 rounded-full text-sm">{index + 1} / {images.length}</div>
        )}
        {/* Main image area — leaves room at the bottom for the thumbnail strip */}
        <div className={`relative w-full ${images?.length > 1 ? 'h-[78vh]' : 'h-[88vh]'} flex items-center justify-center p-4`}>
          {images?.[index] && <img loading="lazy" decoding="async" src={images[index]} alt={`${title} - ${index + 1}`} className="max-w-full max-h-full object-contain" />}
          {images?.length > 1 && (
            <>
              <button onClick={() => setIndex(prev => prev === 0 ? images.length - 1 : prev - 1)} className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 rounded-full p-3 transition-colors" data-testid="lightbox-prev-btn">
                <ChevronLeft className="w-8 h-8 text-white" />
              </button>
              <button onClick={() => setIndex(prev => prev === images.length - 1 ? 0 : prev + 1)} className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 rounded-full p-3 transition-colors" data-testid="lightbox-next-btn">
                <ChevronLeft className="w-8 h-8 text-white rotate-180" />
              </button>
            </>
          )}
        </div>
        {/* Thumbnail strip — rendered BELOW the image, not overlapping it (iter481) */}
        {images?.length > 1 && (
          <div className="w-full bg-black/60 border-t border-white/10 py-2 px-2 flex-shrink-0">
            <div className="flex gap-2 overflow-x-auto justify-center max-w-full">
              {images.map((img, idx) => (
                <button key={`${img}-${idx}`} onClick={() => setIndex(idx)}
                  className={`flex-shrink-0 w-16 h-12 rounded overflow-hidden border-2 transition-all ${idx === index ? 'border-white' : 'border-transparent opacity-60 hover:opacity-100'}`}
                  data-testid={`lightbox-thumb-${idx}`}>
                  <img loading="lazy" decoding="async" src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </DialogContent>
  </Dialog>
);

export const JobApplicationDialog = ({
  open, onOpenChange, listing,
  jobAppForm, setJobAppForm,
  submittingApp, onSubmit,
  uploadingCV, onCVUpload
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2"><Briefcase className="w-5 h-5 text-emerald-600" />Job Application</DialogTitle>
        <DialogDescription>Apply for the {listing?.title} role</DialogDescription>
      </DialogHeader>
      <div className="space-y-4 py-2">
        <div className="bg-emerald-50 rounded-lg p-3 border border-emerald-200">
          <h4 className="font-semibold text-sm text-emerald-800">{listing?.title}</h4>
          <div className="flex flex-wrap gap-2 mt-1">
            {listing?.attributes?.job_type && <Badge variant="outline" className="text-xs">{listing.attributes.job_type}</Badge>}
            {listing?.attributes?.sector && <Badge variant="outline" className="text-xs">{listing.attributes.sector}</Badge>}
            {listing?.attributes?.salary_range && <Badge variant="outline" className="text-xs">£{listing.attributes.salary_range}</Badge>}
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div><Label className="text-sm">Full Name <span className="text-red-500">*</span></Label>
            <Input value={jobAppForm.full_name} onChange={(e) => setJobAppForm(prev => ({ ...prev, full_name: e.target.value }))} placeholder="Full Name" className="mt-1" data-testid="app-fullname" /></div>
          <div><Label className="text-sm">Phone <span className="text-red-500">*</span></Label>
            <Input value={jobAppForm.phone} onChange={(e) => setJobAppForm(prev => ({ ...prev, phone: e.target.value }))} placeholder="07XXX XXX XXX" className="mt-1" data-testid="app-phone" /></div>
        </div>
        <div><Label className="text-sm">Email</Label>
          <Input type="email" value={jobAppForm.email} onChange={(e) => setJobAppForm(prev => ({ ...prev, email: e.target.value }))} placeholder="example@email.com" className="mt-1" data-testid="app-email" /></div>
        <div><Label className="text-sm">Cover Letter / Application Message <span className="text-red-500">*</span></Label>
          <Textarea value={jobAppForm.cover_letter} onChange={(e) => setJobAppForm(prev => ({ ...prev, cover_letter: e.target.value }))} placeholder="Briefly explain why you are suitable for this role..." className="mt-1 min-h-[100px]" data-testid="app-cover-letter" /></div>
        <div><Label className="text-sm">Experience Summary</Label>
          <Textarea value={jobAppForm.experience_summary} onChange={(e) => setJobAppForm(prev => ({ ...prev, experience_summary: e.target.value }))} placeholder="Summarise your relevant work experience..." className="mt-1 min-h-[80px]" data-testid="app-experience" /></div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div><Label className="text-sm">Current Job</Label>
            <Input value={jobAppForm.current_job} onChange={(e) => setJobAppForm(prev => ({ ...prev, current_job: e.target.value }))} placeholder="Your current position" className="mt-1" data-testid="app-current-job" /></div>
          <div><Label className="text-sm">Level of Education</Label>
            <Input value={jobAppForm.education} onChange={(e) => setJobAppForm(prev => ({ ...prev, education: e.target.value }))} placeholder="University / Sixth Form" className="mt-1" data-testid="app-education" /></div>
        </div>
        <div><Label className="text-sm">Skills and Qualifications</Label>
          <Input value={jobAppForm.skills} onChange={(e) => setJobAppForm(prev => ({ ...prev, skills: e.target.value }))} placeholder="Excel, AutoCAD, Welding (comma-separated)" className="mt-1" data-testid="app-skills" /></div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div><Label className="text-sm">Salary Expectation (£)</Label>
            <Input value={jobAppForm.salary_expectation} onChange={(e) => setJobAppForm(prev => ({ ...prev, salary_expectation: e.target.value }))} placeholder="15.000" className="mt-1" data-testid="app-salary" /></div>
          <div><Label className="text-sm">Earliest Start Date</Label>
            <Input type="date" value={jobAppForm.availability_date} onChange={(e) => setJobAppForm(prev => ({ ...prev, availability_date: e.target.value }))} className="mt-1" data-testid="app-availability" /></div>
        </div>
        <div><Label className="text-sm">CV / Resume</Label>
          <div className="mt-1">
            {jobAppForm.cv_url ? (
              <div className="flex items-center gap-2 p-2 bg-green-50 border border-green-200 rounded-lg">
                <FileText className="w-4 h-4 text-green-600" />
                <span className="text-xs text-green-700 flex-1 truncate">{jobAppForm.cv_file_name || 'CV uploaded'}</span>
                <Button variant="ghost" size="sm" className="h-6 text-xs text-red-500" onClick={() => setJobAppForm(prev => ({ ...prev, cv_url: '', cv_file_name: '' }))}>Remove</Button>
              </div>
            ) : (
              <label className="flex items-center gap-2 p-3 border-2 border-dashed rounded-lg cursor-pointer hover:border-blue-400 hover:bg-blue-50/50 transition-colors">
                <Upload className="w-5 h-5 text-slate-400" />
                <span className="text-xs text-muted-foreground">{uploadingCV ? 'Loading...' : 'PDF, DOC, DOCX, JPG (max 10MB)'}</span>
                <input type="file" className="hidden" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" onChange={onCVUpload} disabled={uploadingCV} data-testid="cv-upload-input" />
              </label>
            )}
          </div>
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
        <Button onClick={onSubmit} disabled={submittingApp} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="app-submit-btn">
          {submittingApp ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending...</> : <><Send className="w-4 h-4 mr-2" />Submit Application</>}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);
